import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getLaws } from '@/lib/laws';
import type { Law } from '@/types';
import { useContentZoom } from '@/lib/useContentZoom';
import { ZOOM_VALUES } from '@/lib/contentZoom';

function formatLawDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

export function LawsPage() {
  const zoom = useContentZoom();
  const [laws, setLaws] = useState<Law[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setError('');
    getLaws()
      .then(setLaws)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar leyes.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredLaws = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return laws;
    return laws.filter((law) => {
      const hayTitle = law.title.toLowerCase().includes(q);
      const hayDesc = (law.description || '').toLowerCase().includes(q);
      return hayTitle || hayDesc;
    });
  }, [laws, query]);

  return (
    <main className="lex-page" style={{ zoom: ZOOM_VALUES[zoom] } as React.CSSProperties}>
      {/* Page title */}
      <div style={{ padding: '4px 0 16px' }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          Biblioteca
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
          Leyes
        </h1>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Search + count */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <i className="ri-search-line" aria-hidden style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 14, pointerEvents: 'none' }} />
              <input
                type="search"
                className="lex-input"
                placeholder="Buscar ley o descripción…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ paddingLeft: 32 }}
              />
            </div>
            {!loading && (
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                {filteredLaws.length} leyes
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {error ? (
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 14 }}>{presentLexError(error)}</p>
        ) : null}

        {/* Loading */}
        {loading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
            Cargando leyes…
          </p>
        ) : filteredLaws.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
            No hay coincidencias. Prueba con otro término.
          </p>
        ) : (
          /* Law list — border-bottom separated rows */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {filteredLaws.map((law) => {
              const articleCount = law._count?.articles ?? 0;
              const modDate = formatLawDate(law.publishedAt);

              return (
                <Link
                  key={law.id}
                  to={`/leyes/${law.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 0',
                    borderBottom: '1px solid var(--color-border-soft)',
                    textDecoration: 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Law code / kicker */}
                    <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                      Ley
                    </p>
                    {/* Title */}
                    <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.35 }}>
                      {law.title}
                    </p>
                    {/* Meta */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      {articleCount > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{articleCount} artículos</span>
                      )}
                      {modDate ? (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>DOF: {modDate}</span>
                      ) : null}
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line" style={{ fontSize: 18, color: 'var(--color-text-muted)', flexShrink: 0 }} aria-hidden />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
